import React, { useCallback, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { BOARD_TYPE, TOOL_PEN } from '../../../constants';
import { Badge, Fab, Hidden, IconButton } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import boardManager from '../../../pages/LessonPage/boardManager';
import { GA_EVENT_TREE } from '../../../gaConstants';
import MultiBoardIcon from '../../icons/MultiBoardIcon';
import store from '../../../redux/store';
import { changeTool, raiseHand, shareLesson, userJoinRequest, upgradeSubscriptionDialog } from '../../../redux/actions';
import { CAPABILITIES, hasBooleanPermission } from '../../../shared/Capabilities';
import HandRaisedIcon from '../../icons/HandRaisedIcon';
import socketUtils from '../../../pages/LessonPage/socketUtils';
import AIAssistantStateIcon from '../../AIAssistant/AIAssistantStateIcon';
import AIAssistantPanel from '../../AIAssistant/AIAssistantPanel';
import DraftsIcon from '@material-ui/icons/Drafts';
import { Pause, PlayArrow, Stop } from '@material-ui/icons';
import VBToolTip from '../../Tooltip/VBToolTip';
import PersonAddIcon from '@material-ui/icons/PersonAdd';

// The assistant button mirrors the panel's state, so a teacher who collapsed the panel still
// knows where things stand: the plain face while it waits for input, the animated face while a
// request or a placement runs, and the checked face while an answer is ready but not yet on the
// board. Once the content has been placed the face is plain again.
const aiIconStateFor = (status) => {
    if (status === 'generating' || status === 'placing') return 'working';
    if (status === 'preview') return 'done';
    return 'idle';
};

const ActionButtons = (props) => {
    const [aiPanelOpen, setAiPanelOpen] = useState(false);
    const [aiStatus, setAiStatus] = useState('idle');

    const handleAiStatusChange = useCallback((status) => setAiStatus(status), []);

    const aiButtonIcon = <AIAssistantStateIcon state={aiIconStateFor(aiStatus)} size={28} />;

    const raiseHandClick = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.actions.raiseHand);
        socketUtils.sendRaisedHandValue(!props.lessonState.raisedHand);
        store.dispatch(raiseHand());
    };

    const handleAiButtonClick = () => {
        const hasAI = hasBooleanPermission(
            props.subscriptions,
            props.profile.subscription,
            CAPABILITIES.MATH_CONVERTER,
        );

        if (!hasAI.value) {
            window.logAppActivity(GA_EVENT_TREE.aiPanel.actions.upsellShown);
            store.dispatch(
                upgradeSubscriptionDialog({
                    key: 'subscription.upsell.mathConverter.unavailable',
                    subscriptions: hasAI.availableSubscriptions,
                }),
            );
            return;
        }

        window.logAppActivity(GA_EVENT_TREE.aiPanel.actions.open);
        setAiPanelOpen(true);
    };


    if (!boardManager.isAdmin) {
        return (
            <div style={{ padding: '0.1rem 0.5rem 0.1rem 0.5rem', borderRight: '1px solid rgba(0, 0, 0, 0.1' }}>
                <Hidden mdUp implementation='css'>
                    <Fab
                        size='small'
                        color={props.lessonState.raisedHand ? 'primary' : 'default'}
                        onClick={raiseHandClick}
                    >
                        <HandRaisedIcon />
                    </Fab>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <Button
                        variant='contained'
                        color={props.lessonState.raisedHand ? 'primary' : 'default'}
                        size='small'
                        startIcon={<HandRaisedIcon />}
                        onClick={raiseHandClick}
                    >
                        <FormattedMessage id='menu.raiseHand' />
                    </Button>
                </Hidden>
            </div>
        );
    }

    const resetToPen = () => {
        store.dispatch(changeTool(TOOL_PEN));
        if (window.switchToStickySelection) {
            window.switchToStickySelection(false);
        }
        if (window.toolSelectionCallback) {
            window.toolSelectionCallback(TOOL_PEN);
        }
    };

    const switchToSplitBoards = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.startIndividualBoards);
        boardManager.switchToNewMultiBoardForAdmin();
        resetToPen();
    };

    const resumeIndividualBoard = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.resumeIndividualBoards);
        boardManager.resumeMultiBoardForAdmin();
        resetToPen();
    };

    const switchToSingleBoard = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.stopIndividualBoards);
        boardManager.switchToSingleBoardForAdmin();
        resetToPen();
    };

    const pauseMultiBoard = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.pauseIndividualBoards);
        boardManager.switchToSingleBoardForAdmin(true);
        resetToPen();
    };
    const displayShareDialog = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.share);
        store.dispatch(shareLesson({ id: window.location.pathname.split('/')[2] }));
    };
    const showJoinRequests = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.requests);
        store.dispatch(userJoinRequest({ classId: window.location.pathname.split('/')[2], userId: null }));
    };

    let activeMemberCount = 0;
    if (props.lessonState && props.lessonState.members) {
        props.lessonState.members.forEach((member) => {
            if (member.online) {
                activeMemberCount++;
            }
        });
    }

    if (!boardManager.lessonState) return null;
    if (!props?.lessonState) return null;

    let multiBoardOption;
    if (props?.lessonState?.boardType === BOARD_TYPE.MULTI_BOARD) {
        multiBoardOption = (
            <>
                <Hidden mdUp implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.pauseBoard.tooltip' />}>
                        <IconButton size='small' color='primary' className='u-ml-half' onClick={pauseMultiBoard}>
                            <Pause />
                        </IconButton>
                    </VBToolTip>
                    <VBToolTip content={<FormattedMessage id='menu.stopBoard.tooltip' />}>
                        <IconButton size='small' color='primary' className='u-ml-half' onClick={switchToSingleBoard}>
                            <Stop />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.pauseBoard.tooltip' />}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<Pause />}
                            onClick={pauseMultiBoard}
                            className='u-ml-half'
                        >
                            <FormattedMessage id='menu.pauseBoard' />
                        </Button>
                    </VBToolTip>
                    <VBToolTip content={<FormattedMessage id='menu.stopBoard.tooltip' />}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<Stop />}
                            onClick={switchToSingleBoard}
                            className='u-ml-half'
                        >
                            <FormattedMessage id='menu.stopBoard' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </>
        );
    } else if (props?.lessonState?.boardType === BOARD_TYPE.SINGLE_BOARD && !boardManager.lessonState.pausedBoard) {
        multiBoardOption = (
            <>
                <Hidden mdUp implementation='css'>
                    <VBToolTip
                        content={
                            activeMemberCount === 0 ? (
                                <FormattedMessage id='menu.splitBoard.tooltip.disabled' />
                            ) : (
                                <FormattedMessage id='menu.splitBoard.tooltip' />
                            )
                        }
                    >
                        <IconButton
                            size='small'
                            color='primary'
                            className='u-ml-half'
                            onClick={switchToSplitBoards}
                            disabled={activeMemberCount === 0}
                        >
                            <MultiBoardIcon />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip
                        content={
                            activeMemberCount === 0 ? (
                                <FormattedMessage id='menu.splitBoard.tooltip.disabled' />
                            ) : (
                                <FormattedMessage id='menu.splitBoard.tooltip' />
                            )
                        }
                    >
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<MultiBoardIcon />}
                            onClick={switchToSplitBoards}
                            className='u-ml-half'
                            disabled={activeMemberCount === 0}
                        >
                            <FormattedMessage id='menu.splitBoard' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </>
        );
    } else {
        multiBoardOption = (
            <>
                <Hidden mdUp implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.resumeBoard' />}>
                        <IconButton
                            size='small'
                            color='primary'
                            className='u-ml-half'
                            onClick={resumeIndividualBoard}
                            disabled={activeMemberCount === 0}
                        >
                            <PlayArrow />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.resumeBoard.tooltip' />}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<PlayArrow />}
                            onClick={resumeIndividualBoard}
                            className='u-ml-half'
                            disabled={activeMemberCount === 0}
                        >
                            <FormattedMessage id='menu.resumeBoard' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </>
        );
    }

    return (
        <>
            {props?.lessonState?.openJoinRequests?.length > 0 ? (
                <div style={{ padding: '0.1rem 0.5rem 0.1rem 0.5rem', borderRight: '1px solid rgba(0, 0, 0, 0.1' }}>
                    <Badge badgeContent={props?.lessonState?.openJoinRequests?.length} color='secondary'>
                        <IconButton size='small' color='primary' onClick={showJoinRequests}>
                            <DraftsIcon />
                        </IconButton>
                    </Badge>
                </div>
            ) : null}

            <div style={{ padding: '0.1rem 0.5rem 0.1rem 0.5rem', borderRight: '1px solid rgba(0, 0, 0, 0.1' }}>
                <Hidden mdUp implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.aiExercise.tooltip' />}>
                        <IconButton size='small' color='primary' onClick={handleAiButtonClick} data-ai-assistant-button>
                            {aiButtonIcon}
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.aiExercise.tooltip' />}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={aiButtonIcon}
                            onClick={handleAiButtonClick}
                            data-ai-assistant-button
                        >
                            <FormattedMessage
                                id={
                                    aiStatus === 'generating'
                                        ? 'ai.button.generating'
                                        : aiStatus === 'placing'
                                        ? 'ai.placing.single'
                                        : 'menu.aiExercise'
                                }
                            />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </div>

            <div style={{ padding: '0.1rem 0.5rem 0.1rem 0.5rem', borderRight: '1px solid rgba(0, 0, 0, 0.1' }}>
                <Hidden mdUp implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.shareLesson.tooltip' />}>
                        <IconButton size='small' color='primary' onClick={displayShareDialog}>
                            <PersonAddIcon />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.shareLesson.tooltip' />}>
                        <Button
                            variant='contained'
                            color='primary'
                            size='small'
                            startIcon={<PersonAddIcon />}
                            onClick={displayShareDialog}
                        >
                            <FormattedMessage id='menu.shareLesson' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </div>
            {multiBoardOption}

            <AIAssistantPanel
                open={aiPanelOpen}
                onClose={() => setAiPanelOpen(false)}
                onStatusChange={handleAiStatusChange}
                updateLanguage={props.updateLanguage}
            />
        </>
    );
};

const mapStateToProps = (state) => {
    const { drawing, lessonState, profile, mainAppState } = state;
    return { drawing, lessonState, profile, subscriptions: mainAppState.subscriptions };
};

export default injectIntl(connect(mapStateToProps)(ActionButtons));
